import { toaster } from '@/components/ui/toaster';
import { usePayoutQuery } from '@/hooks/queries/use-payout-query';
import { usePayoutCheckoutData, usePayoutCheckoutStore } from '@/stores/payout-checkout-store';
import { PayoutBatchStatus } from '@fluenceur/types';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorState } from './confirmation/ErrorState';
import { ExpiredState } from './confirmation/ExpiredState';
import { LoadingState } from './confirmation/LoadingState';
import { ProcessingState } from './confirmation/ProcessingState';
import { SuccessState } from './confirmation/SuccessState';

type PayoutConfirmationPageProps = object;

type ConfirmationState = 'loading' | 'processing' | 'success' | 'error' | 'expired';

const PayoutConfirmationPage: React.FC<PayoutConfirmationPageProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { payoutId } = useParams<{ payoutId: string }>();
  const [confirmationState, setConfirmationState] = useState<ConfirmationState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = usePayoutCheckoutStore((state) => state.reset);
  const {
    payout: storePayout,
    payoutScheduleOption,
    scheduledPayoutDate,
  } = usePayoutCheckoutData();

  // Fetch fresh payout data to confirm status
  const query = usePayoutQuery(payoutId!, [
    'affiliatePayouts',
    'involvedAffiliates',
    'transfers',
    'paymentMethod',
  ]);

  // A closed payout will never be paid, so there is nothing left to wait for.
  // Read on the fresh copy: the checkout store still holds the draft the brand
  // opened, and polling on it alone would never stop.
  const isClosed = query.data?.status === PayoutBatchStatus.CANCELLED;

  // Determine if we should poll
  const shouldPoll =
    !isClosed && (confirmationState === 'processing' || storePayout?.status === 'draft');

  // Set up polling
  useEffect(() => {
    if (shouldPoll) {
      const interval = setInterval(() => {
        query.refetch();
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [shouldPoll, query]);

  const { data: freshPayout, isLoading, error } = query;

  // Use fresh payout data if available, otherwise fall back to store data
  const payout = freshPayout || storePayout;

  // Add initial delay to allow webhook processing
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [webhookTimeout, setWebhookTimeout] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 500); // 500ms delay to allow webhook to process

    // Set a timeout for webhook processing
    const timeoutTimer = setTimeout(() => {
      if (payout?.status === 'draft') {
        setWebhookTimeout(true);
      }
    }, 15000); // 15 seconds timeout

    return () => {
      clearTimeout(timer);
      clearTimeout(timeoutTimer);
    };
  }, [payout?.status]);

  useEffect(() => {
    if (!initialLoadComplete) return;

    if (isLoading && !payout) {
      setConfirmationState('loading');
    } else if (error) {
      setConfirmationState('error');
      setErrorMessage(error.message || t('payouts.confirmation.errors.loadFailed'));
    } else if (payout) {
      // Check payout status to determine confirmation state
      if (payout.status === PayoutBatchStatus.DRAFT) {
        // Keep showing processing state while waiting for webhook
        setConfirmationState('processing');
      } else if (payout.status === PayoutBatchStatus.PROCESSING) {
        setConfirmationState('success');
        // Show success toast only once when transitioning to success
        if (confirmationState !== 'success') {
          toaster.create({
            title: t('payouts.confirmation.toast.title'),
            description: t('payouts.confirmation.toast.description'),
            type: 'success',
            duration: 5000,
          });
        }
      } else if (
        payout.status === PayoutBatchStatus.COMPLETED ||
        payout.status === PayoutBatchStatus.PENDING ||
        payout.status === PayoutBatchStatus.PARTIALLY_COMPLETED
      ) {
        setConfirmationState('success');
        // Don't show toast for these states as they happen later
      } else if (payout.status === PayoutBatchStatus.FAILED) {
        setConfirmationState('error');
        setErrorMessage(t('payouts.confirmation.errors.processingFailed'));
      } else if (payout.status === PayoutBatchStatus.CANCELLED) {
        // Closed before it was paid, for whatever reason: the state reads
        // `closedReason` to tell a missed deadline from any other cancellation.
        setConfirmationState('expired');
      }
    }
  }, [isLoading, error, payout, confirmationState, initialLoadComplete, t]);

  // Clean up store on unmount
  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const handleBackToPayouts = () => {
    navigate('/payments/commissions?tab=payouts');
  };

  // The commissions of a closed payout are payable again: the brand is sent to them.
  const handleBackToCommissions = () => {
    navigate('/payments/commissions');
  };

  const formatScheduledDate = () => {
    if (payoutScheduleOption === 'asap') {
      return t('payouts.confirmation.dates.asap');
    }
    if (scheduledPayoutDate) {
      try {
        const date = new Date(scheduledPayoutDate);
        return format(date, 'MMMM d, yyyy');
      } catch {
        return t('payouts.confirmation.dates.scheduled');
      }
    }
    return t('payouts.confirmation.dates.notScheduled');
  };

  // Render appropriate state
  switch (confirmationState) {
    case 'loading':
      return <LoadingState />;

    case 'error':
      return (
        <ErrorState
          errorMessage={errorMessage}
          onBack={handleBackToPayouts}
          onRetry={() => window.location.reload()}
        />
      );

    case 'processing':
      return <ProcessingState webhookTimeout={webhookTimeout} payoutStatus={payout?.status} />;

    case 'expired':
      return (
        <ExpiredState
          closedReason={payout?.closedReason ?? null}
          onBack={handleBackToCommissions}
        />
      );

    case 'success':
      return (
        <SuccessState
          payout={payout}
          payoutScheduleOption={payoutScheduleOption}
          formatScheduledDate={formatScheduledDate}
          onBackToPayouts={handleBackToPayouts}
        />
      );

    default:
      return null;
  }
};

export default PayoutConfirmationPage;
